import React from 'react'
import { DocumentCheckIcon, TableCellsIcon } from '@heroicons/react/24/outline'

import { Badge, Button } from '../../../../components/ui'
import { SourceBadge } from '../../components/ContractBadges'
import { ExtractionCitations } from '../sow/ExtractionCitations'
import { formatCurrency, formatDate } from '../../domain/formatters'
import type {
  ContractDocumentResult,
  ContractExtraction,
  ContractMilestone,
  HubSpotDeal,
  RevenueScheduleLine,
} from '../../types'

export function HubSpotDealsTable({ deals }: { deals: HubSpotDeal[] }) {
  if (deals.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-dark-border dark:bg-dark-surface">
        <p className="text-sm font-medium text-charcoal dark:text-gray-100">No HubSpot deals found</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Deal context appears here when HubSpot has a client match.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
          <thead className="bg-gray-50 dark:bg-dark-surface-2">
            <tr>
              {['Deal', 'Stage', 'Amount', 'Close Date', 'Created'].map(header => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border/70">
            {deals.map((deal, index) => (
              <tr key={deal.id || `${deal.deal_name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2">
                <td className="max-w-[360px] px-4 py-3 font-medium text-charcoal dark:text-gray-100">
                  <span className="line-clamp-2">{deal.deal_name || '-'}</span>
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{deal.stage || '-'}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatCurrency(deal.amount)}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDate(deal.close_date)}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDate(deal.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function ScheduleTable({ schedule }: { schedule: RevenueScheduleLine[] }) {
  if (schedule.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No monthly schedule extracted.</p>
  }

  return (
    <div className="max-h-[360px] overflow-auto rounded-lg border border-gray-200 dark:border-dark-border">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
        <thead className="sticky top-0 bg-gray-50 dark:bg-dark-surface-2">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Month</th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-dark-border/70">
          {schedule.map((line, index) => (
            <tr key={`${line.month}-${index}`}>
              <td className="px-3 py-2 text-charcoal dark:text-gray-100">{line.month || '-'}</td>
              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{formatCurrency(line.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function MilestoneTable({ milestones }: { milestones: ContractMilestone[] }) {
  const rows = milestones.filter(milestone => milestone.amount)
  if (rows.length === 0) return null

  return (
    <div className="max-h-[360px] overflow-auto rounded-lg border border-gray-200 dark:border-dark-border">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
        <thead className="sticky top-0 bg-gray-50 dark:bg-dark-surface-2">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Milestone</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Due Date</th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-dark-border/70">
          {rows.map((milestone, index) => (
            <tr key={`${milestone.name}-${index}`}>
              <td className="px-3 py-2 text-charcoal dark:text-gray-100">{milestone.name || '-'}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                {formatDate(milestone.due_date || milestone.estimated_date)}
              </td>
              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">
                {formatCurrency(milestone.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ContractDetail({ contract }: { contract: ContractDocumentResult }) {
  const [showCitations, setShowCitations] = React.useState(false)
  const ext: ContractExtraction = contract.extraction || {}
  const currency = ext.currency || 'USD'
  const value = formatCurrency(ext.contract_value, currency)
  const sowValue = ext.sow_contract_value
  const discrepancy =
    ext.value_source === 'hubspot' && typeof sowValue === 'number' && typeof ext.contract_value === 'number'
      ? Math.abs(ext.contract_value - sowValue)
      : 0
  const milestones = ext.milestones || []
  const schedule = ext.revenue_schedule || ext.monthly_revenue || []
  const hasMilestones = milestones.some(milestone => milestone.amount)

  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-dark-border">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <DocumentCheckIcon className="h-5 w-5 text-gray-400" />
            <h2 className="truncate text-base font-semibold text-charcoal dark:text-gray-100">
              {contract.file_name || 'Contract document'}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {ext.matched_deal ? `Matched to ${ext.matched_deal}` : contract.mime_type || 'Document'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SourceBadge source={ext.value_source} />
          <Badge variant={hasMilestones ? 'warning' : 'secondary'} className="rounded-md">
            {hasMilestones ? 'Milestone schedule' : 'Monthly schedule'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-[minmax(260px,360px)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Contract Value</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-2xl font-semibold text-charcoal dark:text-gray-100">{value}</p>
              <SourceBadge source={ext.value_source} />
            </div>
            {discrepancy > 1 && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                SOW states {formatCurrency(sowValue, currency)}
              </p>
            )}
          </div>

          <dl className="grid gap-3 text-sm">
            {[
              ['Start Date', formatDate(ext.start_date)],
              ['End Date', formatDate(ext.end_date)],
              ['Payment Terms', ext.payment_terms || '-'],
              ['Invoice Timing', ext.invoice_timing || '-'],
            ].map(([label, valueText]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
                <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="mt-1 text-charcoal dark:text-gray-100">{valueText}</dd>
              </div>
            ))}
          </dl>

          {ext.notes && (
            <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-dark-bg dark:text-gray-300">
              {ext.notes}
            </p>
          )}
          {(ext.citations?.length || 0) > 0 && (
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowCitations(value => !value)}>
                {showCitations ? 'Hide contract citations' : `View contract citations (${ext.citations?.length || 0})`}
              </Button>
              {showCitations && <ExtractionCitations citations={ext.citations || []} />}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TableCellsIcon className="h-5 w-5 text-gray-400" />
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
              {hasMilestones ? 'Payment Schedule' : 'Revenue Schedule'}
            </h3>
          </div>
          {hasMilestones ? <MilestoneTable milestones={milestones} /> : <ScheduleTable schedule={schedule} />}
        </div>
      </div>
    </section>
  )
}
